import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { setDoc, doc, getDocs, collection, getDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import yellowFacesLogo from "../images/yf-symbol.svg";
import invertedCommaLogo from "../images/ic-symbol.svg";
import equatorsLogo from "../images/eq-symbol.svg";
import { MessageCircle, X } from "lucide-react";

function Admin() {
  const [projectId, setProjectId] = useState("");
  const [projectName, setProjectName] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [projects, setProjects] = useState([]);
  const [showTopics, setShowTopics] = useState(false);
  const [allTopics, setAllTopics] = useState({});
  const [comments, setComments] = useState({});
  // const [expandedComments, setExpandedComments] = useState({});
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [isCommentsPanelOpen, setIsCommentsPanelOpen] = useState(false);

  const navigate = useNavigate();
  const teamLogos = {
    equators: equatorsLogo,
    invertedComma: invertedCommaLogo,
    yellowFaces: yellowFacesLogo,
  };

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "Projects"));
        const projectList = [];

        let allComments = {};

        querySnapshot.forEach((docSnap) => {
          const projectData = docSnap.data();
          const teams = projectData.teams || {};
          const votes = projectData.votes || {};

          // Process comments for each team
          let projectComments = {};
          Object.keys(votes).forEach((team) => {
            let teamComments = [];

            Object.values(votes[team] || {}).forEach((userVote) => {
              if (userVote.comment) {
                teamComments.push(userVote.comment); // Store comments anonymously
              }
            });

            projectComments[team] = teamComments;
          });

          allComments[docSnap.id] = projectComments;

          projectList.push({
            id: docSnap.id,
            name: projectData.name,
            teams,
            votes,
          });
        });

        setProjects(projectList);
        setComments(allComments);
      } catch (err) {
        toast.error("Error fetching projects: " + err.message);
      }
    };

    fetchProjects();
  }, []);

  const handleSubmit = async () => {
    if (!projectId || !projectName) {
      toast.error("Both Project ID and Name are required.");
      return;
    }
    try {
      // const teamSnapshot = await getDocs(collection(db, "Teams"));
      // const teamData = {};
      // teamSnapshot.forEach((docSnap) => {
      //   const teamId = docSnap.id;
      //   const teamName = docSnap.data().name;
      //   if (teamId && teamName) {
      //     teamData[teamId] = { name: teamName, AverageRating: 0 }; // Initialize AverageRating
      //   } else {
      //     console.error(`Invalid team data for ID: ${teamId}`, docSnap.data());
      //   }
      // });
      // console.log("Fetched Teams for New Project:", teamData); // Debugging log
      const projectRef = doc(db, "Projects", projectId);
      await setDoc(projectRef, {
        name: projectName,
        // teams: teamData,
      });
      setProjectId("");
      setProjectName("");
      setShowForm(false);
      const querySnapshot = await getDocs(collection(db, "Projects"));
      const projectList = querySnapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      setProjects(projectList);
      toast.success("Project added successfully!");
    } catch (err) {
      console.error("Error adding project:", err);
      toast.error("Error adding project: " + err.message);
    }
  };
  const handleShowTopics = async () => {
    try {
      const topicsDocRef = doc(db, "Topics", "sharedTopics"); // Single document
      const topicsDocSnap = await getDoc(topicsDocRef);

      if (topicsDocSnap.exists()) {
        const topicsData = topicsDocSnap.data();
        delete topicsData.timestamp; // Remove timestamp from display
        setAllTopics(topicsData);
      } else {
        toast.error("No topics found.");
      }
    } catch (err) {
      toast.error("Error fetching topics: " + err.message);
    }
  };

  useEffect(() => {
    handleShowTopics();
  }, []);

  const handleLogout = () => {
    navigate("/");
    toast.success("Logged out successfully!");
  };
  const roundToTwoDecimals = (rating) => {
    return rating ? rating.toFixed(2) : "N/A";
  };

  const openCommentsPanel = (projectId, teamName) => {
    setSelectedProject(projectId);
    setSelectedTeam(teamName);
    setIsCommentsPanelOpen(true);
  };

  const closeCommentsPanel = () => {
    setIsCommentsPanelOpen(false);
    setSelectedProject(null);
    setSelectedTeam(null);
  };

  return (
    <div className="min-h-screen flex flex-col items-center bg-[#f5f7df] ">
      <ToastContainer />

      {/* Header */}
      <div className="w-full flex justify-between items-center mb-6 p-6 bg-[#3c603c] shadow-lg sticky top-0">
        <h1 className="text-2xl font-bold text-[#f5f7df]">Admin Panel</h1>
        <button
          onClick={handleLogout}
          className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all"
        >
          Log Out
        </button>
      </div>

      {/* Buttons */}
      <div className="w-full max-w-3xl p-6 bg-[#3c603c] shadow-lg rounded-lg space-y-6">
        <div className="flex justify-around space-x-4 w-full">
          <button
            onClick={() => {
              setShowForm((prev) => !prev);
              setShowTopics(false);
            }}
            className="px-6 py-3 bg-[#f5f7df] text-[#3c603c] font-semibold rounded-lg hover:bg-[#eaeec3] transition-all w-full sm:w-auto"
          >
            Add Project Details
          </button>
          <button
            onClick={() => {
              setShowForm(false);
              setShowTopics((prev) => !prev);
            }}
            className="px-6 py-3 bg-[#f5f7df] text-[#3c603c] font-semibold rounded-lg hover:bg-[#eaeec3] transition-all w-full sm:w-auto"
          >
            Show Topics
          </button>
        </div>

        {/* Project Form */}
        {showForm && (
          <div className="mt-4 space-y-4">
            <div className="mb-4">
              <label className="block text-lg font-medium text-[#f5f7df]">
                Project ID:
              </label>
              <input
                type="text"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                className="border border-gray-300 p-3 mt-2 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-[#eaeec3]"
                placeholder="Enter project ID"
              />
            </div>
            <div className="mb-6">
              <label className="block text-lg font-medium text-[#f5f7df]">
                Project Name:
              </label>
              <input
                type="text"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                className="border border-gray-300 p-3 mt-2 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-[#eaeec3]"
                placeholder="Enter project name"
              />
            </div>
            <button
              onClick={handleSubmit}
              className="px-6 py-3 bg-[#f5f7df] text-[#3c603c] font-semibold rounded-lg w-full sm:w-auto hover:bg-[#eaeec3] transition-all"
            >
              Submit Project
            </button>
          </div>
        )}

        {/* Topics Section */}
        {showTopics && (
          <div className="flex flex-col items-center mt-6">
            <h2 className="text-xl font-semibold text-[#f5f7df] mb-4">
              Shared Topics
            </h2>
            {Object.keys(allTopics).length > 0 ? (
              <div className="w-full max-w-md bg-[#f5f7df] p-4 rounded-lg shadow-lg">
                {Object.entries(allTopics).map(([userName, topics]) => (
                  <div key={userName} className="mb-4">
                    <p className="text-[#3c603c] font-medium mb-2">
                      {userName}:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {topics.map((topic, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 text-sm bg-[#3c603c] text-[#f5f7df] rounded-md"
                        >
                          {topic}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No topics available.</p>
            )}
          </div>
        )}
      </div>

      {/* Projects Table */}
      <div className="w-full max-w-5xl mt-8 p-6 bg-[#3c603c] shadow-lg rounded-lg">
        <h2 className="text-xl font-bold text-[#f5f7df] mb-4">
          Previous Projects
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border table-auto min-w-[600px]">
            <thead>
              <tr className="bg-[#3c603c]">
                <th className="border px-4 py-2 text-[#f5f7df] text-xs sm:text-sm md:text-base">
                  Project ID
                </th>
                <th className="border px-4 py-2 text-[#f5f7df] text-xs sm:text-sm md:text-base">
                  Project Name
                </th>
                {Object.keys(teamLogos).map((teamName) => (
                  <th key={teamName} className="border px-4 py-2">
                    <img
                      src={teamLogos[teamName]}
                      alt={`${teamName} logo`}
                      className="w-10 h-10 sm:w-14 sm:h-14 mx-auto"
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {projects.map((project) => (
                <tr
                  key={project.id}
                  className="bg-[#3c603c] hover:bg-[#2a472a]"
                >
                  <td className="border px-4 py-2 text-[#f5f7df] text-xs sm:text-sm md:text-base">
                    {project.id}
                  </td>
                  <td className="border px-4 py-2 text-[#f5f7df] text-xs sm:text-sm md:text-base">
                    {project.name}
                  </td>
                  {Object.keys(teamLogos).map((teamName) => {
                    const hasComments =
                      comments[project.id]?.[teamName]?.length > 0;
                    return (
                      <td
                        key={teamName}
                        className="border px-4 py-2 text-center text-[#f5f7df] text-xs sm:text-sm md:text-base"
                      >
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
                          <span>
                            {project.votes?.[teamName]?.avg
                              ? roundToTwoDecimals(project.votes[teamName].avg)
                              : "N/A"}
                          </span>
                          {hasComments && (
                            <button
                              onClick={() =>
                                openCommentsPanel(project.id, teamName)
                              }
                              className="p-1 sm:p-2 rounded-full bg-[#f5f7df] text-[#3c603c] hover:bg-[#eaeec3]"
                            >
                              <MessageCircle size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Side Panel for Comments */}
        {isCommentsPanelOpen && selectedProject && selectedTeam && (
          <div className="fixed right-0 top-0 w-full sm:w-96 h-full bg-white shadow-xl p-6 z-50 overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-lg text-[#3c603c]">Comments</h3>
              <button
                onClick={closeCommentsPanel}
                className="text-gray-600 hover:text-gray-900"
              >
                <X size={24} />
              </button>
            </div>
            {comments[selectedProject][selectedTeam].map((comment, index) => (
              <p key={index} className="text-sm text-[#3c603c] mb-2">
                • {comment}
              </p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
export default Admin;
